import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { assembleAutomations, compileAutomation, type Automation } from '@capture/shared';
import { useFormConfigStore } from '../../form-config/state/store';

interface PanelAutomation {
  /** The in-progress edit buffer. `null` while nothing is loaded (not-found/orphan). */
  automation: Automation | null;
  setAutomation: (next: Automation) => void;
  /** Commits `next` to the store: compiles it to RuleAttachment inputs and
   *  swaps them in for the automation's current members in one atomic write
   *  (`replaceAutomationRules`), which `useDebouncedSave` (mounted by the
   *  layout route) then autosaves. There is no separate panel save button. */
  save: (next: Automation) => void;
  /** True when `automationKey` matches a rule `assembleAutomations` could not
   *  parse into an `Automation` (a group member failed to parse, or a lone
   *  rule doesn't match any known effect). Orphans are never rewritten. */
  orphan: boolean;
  /** Set when the most recent `save()` call's `replaceAutomationRules`
   *  returned `[]` — the store's failure signal (form not found; see
   *  `replaceAutomationRules` in `form-config/state/store.ts`). The draft is
   *  kept (never discarded) so the operator's edit isn't lost. */
  saveError: string | null;
}

/** Resolves the automation `automationKey` names among `automations`. Matching
 *  is by, in order: the URL key as a group id, the DRAFT's adopted group id
 *  (so resolution survives after the URL-key branch is deleted — see C2), then
 *  the URL key as any member rule id. */
function findAutomation(
  automations: Automation[],
  automationKey: string,
  draftGroupId: string | null,
): Automation | null {
  return (
    automations.find((a) => a.groupId === automationKey) ??
    (draftGroupId ? automations.find((a) => a.groupId === draftGroupId) : undefined) ??
    automations.find((a) => a.branches.some((b) => b.ruleId === automationKey)) ??
    null
  );
}

/**
 * Loads the `Automation` identified by `automationKey` (an `automationGroupId`
 * or a single rule id) from `formId`'s current rules, and exposes a local
 * draft + save. Re-seeds the draft when the route identity
 * (`formId`/`automationKey`) changes — or when the draft is still null and the
 * store has since produced the automation (deep-link / hard refresh, C1) — but
 * not on every store write, so the panel's own `save()` calls (which mutate
 * `contentV3`) don't clobber in-progress edits on the next render.
 */
export function usePanelAutomation(automationKey: string, formId: string): PanelAutomation {
  const contentV3 = useFormConfigStore((s) => s.contentV3);
  const replaceAutomationRules = useFormConfigStore((s) => s.replaceAutomationRules);

  const assembled = useMemo(() => {
    const form = contentV3?.forms.find((f) => f.id === formId);
    if (!form) return { automations: [] as Automation[], orphanIds: new Set<string>() };
    const { automations, orphans } = assembleAutomations(form.rules ?? []);
    return { automations, orphanIds: new Set(orphans.map((r) => r.id)) };
  }, [contentV3, formId]);

  const [draft, setDraft] = useState<Automation | null>(() =>
    findAutomation(assembled.automations, automationKey, null),
  );
  const [saveError, setSaveError] = useState<string | null>(null);

  const draftGroupId = draft?.groupId ?? null;
  const { stored, orphan } = useMemo(() => {
    const found = findAutomation(assembled.automations, automationKey, draftGroupId);
    if (found) return { stored: found, orphan: false };
    return { stored: null as Automation | null, orphan: assembled.orphanIds.has(automationKey) };
  }, [assembled, automationKey, draftGroupId]);

  // Re-seed on route identity change (navigating to a different automation) OR
  // when the draft is still null and the store has since produced the
  // automation. A null draft carries no edits to clobber, so re-seeding it when
  // content finally arrives is safe — and is what lets a deep-link / hard
  // refresh (contentV3 null at mount) resolve instead of sticking on not-found.
  const routeKeyRef = useRef(`${formId}:${automationKey}`);
  useEffect(() => {
    const routeKey = `${formId}:${automationKey}`;
    const routeChanged = routeKeyRef.current !== routeKey;
    routeKeyRef.current = routeKey;
    if (routeChanged || (draft === null && stored !== null)) {
      setDraft(stored);
    }
  }, [formId, automationKey, stored, draft]);

  const save = useCallback(
    (next: Automation) => {
      setDraft(next);
      // Remove whatever the automation currently maps to — prefer the stored
      // members (authoritative), falling back to the draft's own rule ids when
      // stored hasn't resolved yet (e.g. mid-adoption).
      const previousIds = (stored ?? next).branches
        .map((b) => b.ruleId)
        .filter((id): id is string => !!id);
      // Builder follow-ups (item b) — compileAutomation throws on an
      // unbuildable draft (zero branches, an unknown check effect key, etc.)
      // rather than returning a result; without a guard that throw would
      // escape the click handler as an uncaught render-time exception
      // instead of surfacing through the existing saveError mechanism.
      let inputs: ReturnType<typeof compileAutomation>;
      try {
        inputs = compileAutomation(next);
      } catch (err) {
        setSaveError(
          err instanceof Error ? err.message : "Couldn't save this automation — it's not valid yet.",
        );
        return;
      }
      const preserveIds = next.branches.map((b) => b.ruleId);
      // `replaceAutomationRules` returns `[]` on failure (the form no longer
      // exists in contentV3) — NOT a legitimate "nothing to create", since
      // `inputs` is always non-empty (compileAutomation throws on 0 branches).
      // Treat `[]` as a failed save: keep the draft and surface the error
      // instead of silently pretending the write landed (C2 — this must NOT be
      // gated on `stored`, or edits vanish once the URL-key branch is deleted).
      const createdIds = replaceAutomationRules(formId, previousIds, inputs, preserveIds);
      if (createdIds.length === 0) {
        setSaveError("Couldn't save this automation — the form it belongs to is missing.");
        return;
      }
      // Adopt compiled reality into the draft: the group id the compile chose
      // and the rule ids the store returned. This keeps the next save targeting
      // the same rows (never minting a fresh group) and lets stored-resolution
      // recover via the adopted group id even after the URL-key branch is gone.
      const adoptedGroupId = (inputs[0]?.config as Record<string, unknown> | undefined)
        ?.automationGroupId;
      setDraft({
        ...next,
        groupId: typeof adoptedGroupId === 'string' ? adoptedGroupId : next.groupId,
        branches: next.branches.map((b, i) => ({ ...b, ruleId: createdIds[i] ?? b.ruleId })),
      });
      setSaveError(null);
    },
    [stored, formId, replaceAutomationRules],
  );

  return { automation: draft, setAutomation: setDraft, save, orphan, saveError };
}
